import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { TrendChart } from '@/versions/v1/modules/_viz';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { ConcentrateQualityPanel } from './ConcentrateQualityPanel';
import { GmMiniRow, LandingStripTabs } from './GmLeadBand';
import { GmCard } from './GmTabs';
import { NEUTRAL_PILL } from './LandingDecisionGate';
import { IssueList } from './IssueList';
import { issueListRows } from './IssueList.transform';
import { seniorMetTrendChart } from './LandingSeries.transform';
import {
  AreaBlocks,
  CascadeList,
  CompoundInsight,
  KbSuggestionList,
  KvRows,
  SectionHead,
} from './SeniorMetTabs';
import { cascadeRows, kbSuggestions, processHealthInsight } from './SeniorMetTabs.transform';
import {
  seniorMetHolderName,
  seniorMetLandingModel,
  smetAreaBlocks,
  smetAtStake,
  smetCascadeMarkers,
  smetHeaderFacts,
  smetHealthMeta,
  smetHypotheses,
  smetKbMiniRow,
  smetReviewedWithApprover,
  smetRuleSetLine,
  smetSignOffs,
  smetStripCells,
  smetTrendMiniRow,
  stewardshipHiddenCount,
  stewardshipVisibleRows,
  type SeniorMetTabId,
} from './SeniorMetLanding.transform';

export type { SeniorMetTabId } from './SeniorMetLanding.transform';

const LINK =
  'whitespace-nowrap border-b border-[var(--hairline-strong)] text-[12.5px] font-semibold text-ink-primary hover:border-ink-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]';

/** The dashed neutral `Draft — not live until approved` pill (adjudication 6). */
const DRAFT_PILL =
  'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-2 py-px text-[11px] font-medium leading-4 text-ink-secondary';

// ---- Header facts ------------------------------------------------------------------

/** Comp C's two header facts beside the page title: the rule set in force and
 *  the ore zone with its day count. */
export function SeniorMetHeaderFacts({
  state,
  advisory,
  projectSlug,
}: {
  state: SessionState;
  advisory: AdvisorySessionState;
  projectSlug: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2" data-smet-header-facts>
      {smetHeaderFacts(state, advisory, projectSlug).map((fact) => (
        <span
          key={fact.id}
          className="inline-block whitespace-nowrap rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2.5 py-[3px] text-xs tabular-nums text-ink-secondary"
          data-smet-header-fact={fact.id}
        >
          <span className="font-semibold text-ink-primary">{fact.lead}</span>
          {fact.rest}
        </span>
      ))}
    </div>
  );
}

// ---- Lead band ---------------------------------------------------------------------

function LeadBand({
  state,
  advisory,
  projectSlug,
  tab,
  onTabChange,
}: {
  state: SessionState;
  advisory: AdvisorySessionState;
  projectSlug: string;
  tab: SeniorMetTabId;
  onTabChange: (tab: SeniorMetTabId) => void;
}) {
  const atStake = smetAtStake(state, projectSlug);
  return (
    <div className="grid grid-cols-12 gap-3" data-smet-lead-band>
      <GlassCard
        as="nav"
        tone="matte"
        aria-label="Landing views"
        className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
      >
        <LandingStripTabs
          cells={smetStripCells(state, advisory, projectSlug)}
          selected={tab}
          onSelect={onTabChange}
          dataPrefix="smet"
          panelId="smet-landing-panel"
          columnsClassName="sm:grid-cols-4"
        />
      </GlassCard>
      <Link
        to={`/projects/${projectSlug}/advisory`}
        data-smet-at-stake
        aria-label={`${atStake.label} · ${atStake.figure}${atStake.unit} · open Advisory`}
        className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
      >
        <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{atStake.label}</span>
        <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
          <span data-smet-at-stake-figure>{atStake.figure}</span>
          <span className="text-xs font-semibold tracking-normal text-muted">{atStake.unit}</span>
        </span>
        <span className="text-xs leading-snug tabular-nums text-muted" data-smet-at-stake-comparison>
          <span className="font-semibold text-ink-secondary">{atStake.comparisonLead}</span>
          {atStake.comparisonRest}
        </span>
        <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[12.5px] font-semibold text-ink-primary">
          Advisory
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </span>
      </Link>
    </div>
  );
}

// ---- Process health ----------------------------------------------------------------

function HealthView({ state }: { state: SessionState }) {
  return (
    <div className="grid grid-cols-12 items-stretch gap-3" data-smet-view="health">
      <Commentable name="ProcessHealth">
        <GmCard
          title="Process health by area"
          meta={<span className="font-medium" data-smet-health-meta>{smetHealthMeta(state)}</span>}
          className="col-span-12 lg:col-span-7"
          data-smet-health
        >
          <AreaBlocks blocks={smetAreaBlocks(state)} />
          <div className="mt-2.5">
            <CompoundInsight insight={processHealthInsight(state)} />
          </div>
        </GmCard>
      </Commentable>
      <div className="col-span-12 min-w-0 lg:col-span-5">
        <ConcentrateQualityPanel />
      </div>
    </div>
  );
}

// ---- 7-day trend analysis ----------------------------------------------------------

function TrendView({ state }: { state: SessionState }) {
  const chart = seniorMetTrendChart(state);
  return (
    <div className="space-y-3" data-smet-view="trends">
      <GmMiniRow items={smetTrendMiniRow(state)} />
      <div className="grid grid-cols-12 items-stretch gap-3">
        <Commentable name="ReagentTrend">
          <GmCard
            title="7-day rolling KPI trends"
            meta={<span className="font-medium">daily · dashed = target</span>}
            className="col-span-12 lg:col-span-7"
            data-smet-trend-chart
          >
            <TrendChart
              kind="line"
              categories={chart.categories}
              series={chart.series}
              references={chart.references}
              markers={smetCascadeMarkers(state)}
              markerShape="badge"
              leftYDomain={chart.leftYDomain}
              rightYDomain={chart.rightYDomain}
              height={236}
              distinctSharedTones
              referenceLabelInset={10}
              referenceLabelPlacement="clear"
              legendNote="1–5 cascade steps"
            />
          </GmCard>
        </Commentable>
        <GmCard
          title="Upstream to downstream cascade"
          meta={<span className="font-medium">7-day sequence of events</span>}
          className="col-span-12 lg:col-span-5"
          data-smet-cascade-card
        >
          <CascadeList rows={cascadeRows(state)} />
        </GmCard>
      </div>
    </div>
  );
}

// ---- Diagnostic cases --------------------------------------------------------------

function DiagView({
  state,
  advisory,
  projectSlug,
  onOpenCase,
}: {
  state: SessionState;
  advisory: AdvisorySessionState;
  projectSlug: string;
  onOpenCase: (caseId: string) => void;
}) {
  const signOffs = smetSignOffs(advisory, projectSlug);
  const reviewed = smetReviewedWithApprover(advisory, state);
  const [signOffsExpanded, setSignOffsExpanded] = useState(false);
  const signOffsVisible = stewardshipVisibleRows(signOffs, signOffsExpanded);
  const hiddenSignOffs = stewardshipHiddenCount(signOffs, signOffsExpanded);
  const hypotheses = smetHypotheses();
  const holder = seniorMetHolderName();
  return (
    <div className="grid grid-cols-12 items-start gap-3" data-smet-view="diag">
      <Commentable name="OpenMetCases">
        <GmCard
          title="Diagnostic cases"
          meta={
            <>
              <span className="font-medium">ranked by EBITDA</span>
              <ActorChip actor="Decision model" />
            </>
          }
          className="col-span-12 lg:col-span-8"
          data-smet-cases
        >
          <IssueList rows={issueListRows('smet', projectSlug, state)} roleLabel="Senior metallurgist" onOpenCase={onOpenCase} />
        </GmCard>
      </Commentable>
      <div className="col-span-12 flex min-w-0 flex-col gap-3 lg:col-span-4">
        <GmCard
          title="Waiting on your sign-off"
          meta={
            <>
              <ActorChip actor="Human">{`${holder} · Senior met`}</ActorChip>
              <span className={NEUTRAL_PILL} data-smet-sign-off-count>
                {signOffs.length}
              </span>
            </>
          }
          data-smet-sign-offs
        >
          <ul>
            {signOffsVisible.map((row) => (
              <li key={row.id} className="border-b border-[var(--color-divider)] py-2 first:pt-0.5 last:border-b-0" data-smet-sign-off={row.id}>
                <p className="text-[12.5px] font-medium leading-snug text-ink-primary">{row.summary}</p>
                <p className="mt-1 flex items-baseline gap-2 text-[11.5px] tabular-nums text-muted">
                  <span className="min-w-0">
                    <span className="font-semibold text-ink-secondary">{row.id}</span> · {row.kindLabel}
                  </span>
                  <span className="flex-1" />
                  <Link to={row.href} className={LINK}>
                    Review →
                  </Link>
                </p>
              </li>
            ))}
          </ul>
          {!signOffsExpanded && hiddenSignOffs > 0 ? (
            <button type="button" onClick={() => setSignOffsExpanded(true)} className={`${LINK} mt-1 inline-block`}>
              Show {hiddenSignOffs} more review{hiddenSignOffs === 1 ? '' : 's'} →
            </button>
          ) : null}
          {reviewed ? (
            <div className="mt-2 border-t border-[var(--color-divider)] pt-2" data-smet-reviewed={reviewed.id}>
              <SectionHead className="mt-0">Reviewed · with the approver</SectionHead>
              <p className="mt-1 text-[12.5px] font-medium text-ink-primary">{reviewed.title}</p>
              <p className="mt-0.5 text-[11.5px] tabular-nums text-muted" data-smet-reviewed-status>
                {reviewed.status}
              </p>
              <p className="mt-1 flex items-baseline gap-2 text-[12px] text-ink-secondary">
                <span className="min-w-0">{reviewed.pathway}</span>
                <span className="flex-1" />
                <button type="button" className={LINK} onClick={() => onOpenCase(reviewed.caseId)}>
                  Open case →
                </button>
              </p>
            </div>
          ) : null}
        </GmCard>
        <GmCard
          title="Competing hypotheses"
          meta={<span className={NEUTRAL_PILL}>{hypotheses.length} active</span>}
          data-smet-hypotheses
        >
          <KvRows
            rows={hypotheses.map((row) => ({
              id: row.id,
              left: row.versus ? (
                <>
                  {row.label} <span className="text-muted">vs</span> {row.versus}
                </>
              ) : (
                row.label
              ),
              right: row.strong ? (
                <span className="font-semibold text-ink-primary">{row.figure}</span>
              ) : (
                <span className="text-xs text-muted">{row.figure}</span>
              ),
            }))}
          />
        </GmCard>
      </div>
    </div>
  );
}

// ---- KB update suggestions ---------------------------------------------------------

function KbView({
  state,
  advisory,
  projectSlug,
}: {
  state: SessionState;
  advisory: AdvisorySessionState;
  projectSlug: string;
}) {
  const model = seniorMetLandingModel(state, advisory, projectSlug);
  const ruleSet = smetRuleSetLine(advisory);
  const [stewardshipExpanded, setStewardshipExpanded] = useState(false);
  const [proposedExpanded, setProposedExpanded] = useState(false);
  const stewardshipVisible = stewardshipVisibleRows(model.stewardshipReviews, stewardshipExpanded);
  const hiddenStewardship = stewardshipHiddenCount(model.stewardshipReviews, stewardshipExpanded);
  const proposedVisible = stewardshipVisibleRows(model.proposedRows, proposedExpanded);
  const hiddenProposed = stewardshipHiddenCount(model.proposedRows, proposedExpanded);
  return (
    <div className="space-y-3" data-smet-view="kb">
      <GmMiniRow items={smetKbMiniRow(state)} />
      <div className="grid grid-cols-12 items-stretch gap-3">
        <GmCard
          title="KB update suggestions"
          meta={<ActorChip actor="Agent" />}
          className="col-span-12 lg:col-span-7"
          data-smet-kb-card
        >
          <KbSuggestionList suggestions={kbSuggestions()} />
        </GmCard>
        <Commentable name="RuleSetStewardship">
          <GmCard title="Rule-set stewardship" className="col-span-12 lg:col-span-5" data-smet-stewardship>
            <div id="rule-set-stewardship">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2.5 gap-y-1 border-b border-[var(--color-divider)] pb-2 text-[12.5px] tabular-nums">
                <span className="text-ink-secondary">
                  <span className="whitespace-nowrap font-semibold text-ink-primary">{ruleSet.lead}</span>
                  <span className="whitespace-nowrap">{ruleSet.rest}</span>
                </span>
                <Link to={`/projects/${projectSlug}/advisory?tab=rule-set`} className={LINK}>
                  Advisory › Rule set ↗
                </Link>
              </div>
              <SectionHead className="mt-2.5">FMEA reviews at your seat · {model.stewardshipReviews.length}</SectionHead>
              <ul>
                {stewardshipVisible.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-baseline justify-between gap-2.5 border-b border-[var(--color-divider)] py-[7px] text-[12.5px]"
                    data-smet-review={row.id}
                  >
                    <Link to={row.href} className="min-w-0 font-medium text-ink-primary hover:underline">
                      {row.summary}
                    </Link>
                    <span className="shrink-0 text-[11.5px] tabular-nums text-muted">{row.id}</span>
                  </li>
                ))}
              </ul>
              {!stewardshipExpanded && hiddenStewardship > 0 ? (
                <button type="button" onClick={() => setStewardshipExpanded(true)} className={`${LINK} mt-2 inline-block`}>
                  Show {hiddenStewardship} more review{hiddenStewardship === 1 ? '' : 's'} →
                </button>
              ) : null}
              {model.proposedRows.length > 0 ? (
                <>
                  <SectionHead className="mt-3">Proposed rows from signal-led cases · {model.proposedRows.length}</SectionHead>
                  <ul>
                    {proposedVisible.map((row) => (
                      <li
                        key={row.id}
                        className="flex items-baseline justify-between gap-2.5 border-b border-[var(--color-divider)] py-[7px] text-[12.5px]"
                        data-smet-proposed={row.id}
                      >
                        <Link to={row.href} className="min-w-0 font-medium text-ink-primary hover:underline">
                          {row.summary}
                        </Link>
                        <span className={DRAFT_PILL}>Draft — not live until approved</span>
                      </li>
                    ))}
                  </ul>
                  {!proposedExpanded && hiddenProposed > 0 ? (
                    <button type="button" onClick={() => setProposedExpanded(true)} className={`${LINK} mt-2 inline-block`}>
                      Show {hiddenProposed} more proposed row{hiddenProposed === 1 ? '' : 's'} →
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
          </GmCard>
        </Commentable>
      </div>
    </div>
  );
}

// ---- Composition -------------------------------------------------------------------

type SeniorMetLandingProps = {
  projectSlug: string;
  sessionState: SessionState;
  advisory: AdvisorySessionState;
  onOpenCase: (caseId: string) => void;
  tab: SeniorMetTabId;
  onTabChange: (tab: SeniorMetTabId) => void;
};

/**
 * The Senior metallurgist's landing (polish.P27 #714, locked comp
 * `landing-senior-met.html` A, finished with B's sign-off card and headroom
 * meters and C's two header facts): the GM landing's anatomy — four strip
 * cells that are the tab bar beside the Active cases link — then the view.
 * The default view sets the five plant areas and the compound cause beside
 * concentrate quality and losses, in Paul's region order.
 */
export function SeniorMetLanding({
  projectSlug,
  sessionState,
  advisory,
  onOpenCase,
  tab,
  onTabChange,
}: SeniorMetLandingProps) {
  const model = seniorMetLandingModel(sessionState, advisory, projectSlug);
  return (
    <div className="space-y-3" data-smet-landing>
      <p className="-mt-3 text-sm text-muted">{model.title}</p>
      <Commentable name="Kpis">
        <LeadBand state={sessionState} advisory={advisory} projectSlug={projectSlug} tab={tab} onTabChange={onTabChange} />
      </Commentable>
      <div id="smet-landing-panel" role="tabpanel" aria-labelledby={`smet-tab-${tab}`}>
        <Commentable name="Primary">
          {tab === 'health' ? <HealthView state={sessionState} /> : null}
          {tab === 'trends' ? <TrendView state={sessionState} /> : null}
          {tab === 'diag' ? <DiagView state={sessionState} advisory={advisory} projectSlug={projectSlug} onOpenCase={onOpenCase} /> : null}
          {tab === 'kb' ? <KbView state={sessionState} advisory={advisory} projectSlug={projectSlug} /> : null}
        </Commentable>
      </div>
    </div>
  );
}
